/**
 * App entry point. NOTE (DEC-016): MSW is intentionally NOT started here — it was the
 * prototype's entire backend, but the real `/api/v1` exists now and MSW is removed from
 * the runtime (R-18.159 keeps it at the test boundary only). The app talks to the real
 * API via the shared client.
 *
 * `@/shared/lib/i18n` is imported as a side effect to initialize react-i18next before
 * render (R-18.317). A top-level `unhandledrejection` listener reports stray promise
 * rejections (R-18.66).
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from '@/App';
import {
  checkBundleFreshness,
  installStaleChunkAutoReload,
} from '@/shared/lib/staleChunkReload';

import '@/shared/lib/i18n';
import 'antd/dist/reset.css';

window.addEventListener('unhandledrejection', (event) => {
  // R-18.66 — log/report unhandled promise rejections at app startup. Single seam:
  // swap console for the real reporter (Sentry) when observability is wired.
  console.error('[unhandledrejection]', event.reason);
});

// A redeploy replaces every content-hashed chunk; tabs holding the previous
// index.html self-heal with a one-shot reload instead of wedging on the fallback,
// and every session start verifies the booted bundle against the live index.html.
installStaleChunkAutoReload();
void checkBundleFreshness();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
